import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert } from 'app/shared/form-alert';
import { Icon } from 'assets/icons';
import { generateUniqueId } from 'app/shared/utils/general';
import { useGuestService } from 'hooks/guest';
import { urls } from 'services/_url';
import styles from './password.module.css';

const PasswordReset = () => {
  const { id } = useParams();
  const { resetPassword } = useGuestService();

  const [alert, setAlert] = useState({});
  const [form_data, setFormData] = useState({ password: '' });
  const [is_loading, setIsLoading] = useState(false);
  const [show_password, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    password: ''
  });
  const history = useNavigate();

  const validatePassword = (value) => {
    let errorMessage = '';
    if (!value.trim()) {
      errorMessage = 'Password is required.';
    } else if (value.length < 8) {
      errorMessage = 'Password must be at least eight characters long.';
    }
    setErrors({ password: errorMessage });
  };

  const handlePasswordChange = (value) => {
    setFormData({ password: value });
    validatePassword(value);
  };

  const submitForm = () => {
    if (disableSubmitButton) {
      validatePassword(form_data.password);
      return;
    }

    const data = {
      recovery_id: id,
      password: form_data.password
    };

    setIsLoading(true);
    resetPassword({ data })
      .then((response) => {
        const { error } = response;

        if (error) {
          setAlert({ message: error, id: generateUniqueId() });
          return;
        }

        history('/password/success');
      })
      .finally(() => setIsLoading(false));
  };

  const toggleShowPassword = () => {
    setShowPassword(!show_password);
  };

  const isFormDisabled = (errors, values) => {
    const hasErrors = Object.values(errors).some((error) => error !== '');
    const isFieldsIncomplete = Object.values(values).some((value) => !value.trim());
    return hasErrors || isFieldsIncomplete;
  };

  const disableSubmitButton = isFormDisabled(errors, { password: form_data.password });

  return (
    <div className={styles.pageWrapper}>
      <a href={urls.landing} className={styles.homeBtn}>
        <Icon name="home" width={16} height={16} />
        Home
      </a>

      <div className={styles.formInner}>
        <h1 className={styles.formHeading}>Set new password</h1>
        <p className={styles.formSubhead}>Choose a strong password to secure your account.</p>

        {alert.message && (
          <div className={styles.alertWrap}>
            <Alert {...alert} />
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitForm();
          }}
        >
          <div className={styles.fieldGroup}>
            <div>
              <label className={styles.fieldLabel} htmlFor="reset-password">
                New password
              </label>
              <div className={styles.inputPwWrap}>
                <input
                  id="reset-password"
                  type={show_password ? 'text' : 'password'}
                  autoComplete="new-password"
                  autoFocus
                  placeholder="At least 8 characters"
                  value={form_data.password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  className={`${styles.input} ${styles.inputPwPad}${
                    errors.password ? ` ${styles.inputError}` : ''
                  }`}
                />
                <button
                  type="button"
                  className={styles.pwToggle}
                  onClick={toggleShowPassword}
                  tabIndex={-1}
                  aria-label={show_password ? 'Hide password' : 'Show password'}
                >
                  <Icon name={show_password ? 'hide' : 'show'} width={18} height={18} />
                </button>
              </div>
              {errors.password && <p className={styles.fieldError}>{errors.password}</p>}
            </div>
          </div>

          <button type="submit" className={styles.submitBtn} disabled={is_loading}>
            {is_loading ? 'Saving…' : 'Reset password →'}
          </button>
        </form>

        <a className={styles.backLink} onClick={() => history('/login')}>
          ← Back to log in
        </a>
      </div>
    </div>
  );
};

export default PasswordReset;
